import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Input,
    ListGroup,
    ListGroupItem,
    Container,
    Row,
    Col,
    Button,
    Pagination,
    PaginationItem,
    Table,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Card,
    CardBody,
    Label,
    FormGroup,
    Badge
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { productActions } from '../../sagas/productSlice';
import { debounce } from 'lodash';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaCalendarAlt, FaEnvelope, FaHistory, FaMapMarkerAlt, FaMinus, FaMoneyBillWave, FaPlus, FaPrint, FaReceipt, FaSearch, FaShoppingCart, FaStore, FaTrash, FaUndoAlt, FaUser, FaWhatsapp } from 'react-icons/fa'; // Import trash icon
import { refundActions } from '../../sagas/refundSlice';
import { useNavigate } from 'react-router-dom';
import { se } from 'date-fns/locale';

const RefundForm = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unites
    const [refundItems, setRefundItems] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [numBL, setNumBL] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [criteria, setSearchCriteria] = useState({ query: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);

    const {
        products,
        loading,
        totalPages,
        totalElements,
        pageNumber,
        pageSize,
        error,
    } = useSelector((state) => state.products);


    useEffect(() => {
        dispatch(productActions.findAllUnites());
    }, []);

    useEffect(() => {
        let payload = {
            search: '',
            page: currentPage,
            size: 10,
        };
        dispatch(productActions.findAll(payload));
    }, [currentPage]);

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [products]);

    const debouncedSearch = useCallback(
        debounce((query) => {
            setIsSearching(true);
            dispatch(productActions.findAll({
                search: query,
                page: currentPage - 1,
                size: pageSize,
                onSuccess: () => setIsSearching(false)
            }));
        }, 500), // Wait 500ms after last keystroke
        []
    );

    const formatPrice = (amount) => {
        const formattedPrice = new Intl.NumberFormat('fr-MA', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
        return `${formattedPrice} DH`;
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const handleAddProduct = (product) => {
        const existingItem = refundItems.find(item => item.productId === product.id);
        if (existingItem) {
            handleUpdateQuantity(existingItem.productId, existingItem.quantity + 1);
        } else {
            setRefundItems([...refundItems, {
                productId: product.id,
                productDescription: product.categorieLibelle + " - " + product.libelle,
                price: product.prixVenteTTC1,
                productUnite: product.unite,
                quantity: 1,
                total: product.prixVenteTTC1
            }]);
        }
    };

    const handleUpdateQuantity = (productId, newQuantity) => {
        setRefundItems(refundItems.map(item =>
            item.productId === productId
                ? { ...item, quantity: newQuantity, total: item.price * newQuantity }
                : item
        ));
    };

    const handleUpdatePrice = (productId, newPrice) => {
        if (newPrice < 0) return;
        setRefundItems(refundItems.map(item =>
            item.productId === productId
                ? { ...item, price: newPrice, total: newPrice * item.quantity }
                : item
        ));
    };

    const handleRemoveItem = (productId) => {
        setRefundItems(refundItems.filter(item => item.productId !== productId));
    };

    const handleSubmit = async () => {
        try {
            if (!numBL || numBL.trim() === '') {
                Swal.fire({
                    icon: 'warning',
                    title: t('refund.missingBLTitle', 'Numéro BL requis'),
                    text: t('refund.missingBLText', "Veuillez saisir le numéro BL avant d'envoyer le retour."),
                });
                return;
            }

            dispatch(refundActions.addRefund(
                {
                    data: { numBL, items: refundItems, total: totalAmount },
                    onSuccess: () => {
                        setRefundItems([]);
                        setTotalAmount(0);
                        setNumBL('');
                        Swal.fire({
                            title: t('refund.successTitle'),
                            text: t('refund.successMessage'),
                            icon: 'success'
                        });

                    }
                }
            ));



            // Generate and print bill
            //handlePrintBill(result.data);
        } catch (error) {
            Swal.fire({
                title: t('refund.errorTitle'),
                text: error.message,
                icon: 'error'
            });
        }
    };

    useEffect(() => {
        const total = refundItems.reduce((sum, item) => sum + item.total, 0);
        setTotalAmount(total);
    }, [refundItems]);

    return (
        <Container fluid>
            <Row>
                <Col md={6} className="pe-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                {/* Left side - Title */}
                                <h4 className="mb-0">
                                    <FaUndoAlt className="text-primary me-2" />
                                    {t('nav.refund')}
                                </h4>
                                <Button
                                    color="primary"
                                    className="d-flex align-items-center"
                                    onClick={() => navigate('/refund/history')}
                                    title={t('refund.viewHistory')}
                                >
                                    <FaHistory className="me-2" />
                                    {t('refund.viewHistory')}
                                </Button>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                    <div className="mt-2 text-muted">
                                        {t('common.loadingData')}
                                    </div>
                                </div>
                            ) : (<>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    {/* Search Input - Left Side */}
                                    <div className="position-relative flex-grow-1 me-3">
                                        <Input
                                            innerRef={searchInputRef}
                                            type="text"
                                            placeholder={t('product.searchFocusHint')}
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                                            title={t('product.searchShortcut')}
                                        />
                                        <FaSearch
                                            style={{
                                                position: 'absolute',
                                                left: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#adb5bd'
                                            }}
                                            size={14}
                                        />
                                        <FaBarcode
                                            style={{
                                                position: 'absolute',
                                                right: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#adb5bd'
                                            }}
                                            size={20}
                                            title={t('product.scanHint')}
                                        />
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <Table bordered hover>
                                        <thead>
                                            <tr>
                                                <th>{t('product.code')}</th>
                                                <th>{t('product.description')}</th>
                                                <th>{t('product.salePrice')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!products || products.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {t('product.noProducts')}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (products.map((product) => (
                                                <tr key={product.id} onClick={() => handleAddProduct(product)}>
                                                    <td>{product.code}</td>
                                                    <td>{product.categorieLibelle} - {product.libelle}</td>
                                                    <td className="align-middle text-end">
                                                        {formatPrice(product.prixVenteTTC1, false)} / {unites.find(u => u.code == product.unite)?.libelle}
                                                    </td>                                                </tr>
                                            ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        {t('product.showing', {
                                            start: (currentPage - 1) * pageSize + 1,
                                            end: Math.min(currentPage * pageSize, totalElements),
                                            total: totalElements,
                                            currentPage: currentPage,
                                            totalPages: totalPages
                                        })}
                                    </small>
                                    <Pagination size="sm" className="mb-0">
                                        <PaginationItem disabled={currentPage === 1}>
                                            <PaginationLink first onClick={() => handlePageChange(1)}>
                                                <FaAngleDoubleLeft size={12} />
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPage === 1}>
                                            <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)}>
                                                <FaAngleLeft size={12} />
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPage === totalPages}>
                                            <PaginationLink next onClick={() => handlePageChange(currentPage + 1)}>
                                                <FaAngleRight size={12} />
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPage === totalPages}>
                                            <PaginationLink last onClick={() => handlePageChange(totalPages)}>
                                                <FaAngleDoubleRight size={12} />
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                                </div>
                            </>)}
                        </CardBody>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card>
                        <CardBody>
                            <FormGroup>
                                <Label for="numBL" className="fw-semibold">{t('refund.numBL', 'N° BL')}</Label>
                                <Input
                                    id="numBL"
                                    type="text"
                                    value={numBL}
                                    onChange={(e) => setNumBL(e.target.value)}
                                    required
                                    placeholder={t('refund.numeroBLPlaceholder', 'Entrez le numéro BL')}
                                />
                            </FormGroup>
                            <div className="cart-container" style={{
                                height: '500px',
                                overflowY: 'auto'
                            }}>
                                <Table hover responsive className="mb-0 cart-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '25%' }}>{t('refund.article')}</th>
                                            <th style={{ width: '20%' }}>{t('refund.quantity')}</th>
                                            <th style={{ width: '20%' }}>{t('refund.unite')}</th>
                                            <th style={{ width: '22%' }}>{t('refund.price')}</th>
                                            <th style={{ width: '23%' }}>{t('refund.total')}</th>
                                            <th style={{ width: '10%' }}>{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {refundItems.map(item => (
                                            <tr key={item.productId}>
                                                <td style={{ width: '25%' }}>{item.productDescription}</td>
                                                <td style={{ width: '20%' }}>
                                                    <div className="position-relative">
                                                        <Input
                                                            type="number"
                                                            name="quantity"
                                                            value={item.quantity}
                                                            onSelect={(e) => e.target.select()}
                                                            onChange={(e) => handleUpdateQuantity(item.productId, e.target.value)}
                                                            className="pe-4"
                                                        />
                                                    </div>
                                                </td>
                                                <td style={{ width: '20%' }}>{unites.find(u => u.code == item.productUnite)?.libelle}</td>
                                                <td>
                                                    <Input
                                                        type="number"
                                                        name="price"
                                                        onSelect={(e) => e.target.select()}
                                                        value={item.price}
                                                        onChange={(event) => handleUpdatePrice(item.productId, parseFloat(event.target.value))}
                                                    />
                                                </td>
                                                <td>{formatPrice(item.total)}</td>
                                                <td>
                                                    <Button color="danger" size="sm" onClick={() => handleRemoveItem(item.productId)}>
                                                        <FaTrash />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <h5>{t('refund.total')}: {formatPrice(totalAmount)}</h5>
                                <Button
                                    color="primary"
                                    disabled={refundItems.length === 0 || !numBL || numBL.trim() === ''}
                                    onClick={handleSubmit}
                                >
                                    {t('refund.submit')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default RefundForm;